import { takeLatest, call, put, all } from 'redux-saga/effects';
import { CloudActions, LicenseServiceUrls } from '@portal/portal-core';

import { UserClientUtils } from 'ducks/user-client';
import {
  deactivateAuthorizedDeviceResult,
  renameAuthorizedDeviceResult,
  createAuthorizedDeviceResult,
  createExtendedOfflineTokenResult,
  exportExtendedOfflineTokenResult,
  DEACTIVATE_AUTHORIZED_DEVICE,
  RENAME_AUTHORIZED_DEVICE,
  CREATE_AUTHORIZED_DEVICE,
  CREATE_EXTENDED_OFFLINE_TOKEN,
  EXPORT_EXTENDED_OFFLINE_TOKEN_SAGA,
} from '../actions/authorized-devices';

// Boundary type: the client config threaded through the root saga wrapper.
// `baseApiUrl` is read here; `getAuthToken` is forwarded to `UserClientUtils.sendAction`.
interface ClientConfig {
  baseApiUrl?: string;
  getAuthToken?: () => string | null | undefined;
}

// Boundary type: shape returned by `UserClientUtils.sendAction` (untyped `.js`).
// See ducks/user-client/utils `wrapHttpRequest`.
interface SendActionResult {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  data?: unknown;
}

// Each action carries `type` so it satisfies redux-saga's `Action` shape, which
// `takeLatest(pattern, worker, ...args)` requires to match the string pattern.
interface DeactivateAuthorizedDeviceAction {
  type: string;
  payload: {
    userId: string;
    deviceId: string;
  };
}

interface RenameAuthorizedDeviceAction {
  type: string;
  payload: {
    userId: string;
    deviceId: string;
    newName: string;
  };
}

interface CreateAuthorizedDeviceAction {
  type: string;
  payload: {
    userId: string;
    costCenterId: string;
    productAccessId: string;
    fingerprint: string;
    deviceName: string;
  };
}

interface CreateExtendedOfflineTokenAction {
  type: string;
  payload: {
    userId: string;
    authorizedDeviceId: string;
    startDate: unknown;
    endDate: unknown;
    productAccessId: string;
  };
}

interface ExportExtendedOfflineTokenAction {
  type: string;
  payload: {
    primaryEmail: string;
    password: string;
    costCenterId: string;
    productAccessId: string;
    eotId: string;
  };
}

function* deactivateAuthorizedDeviceSaga(
  config: ClientConfig,
  action: DeactivateAuthorizedDeviceAction,
): Generator<unknown, void, SendActionResult> {
  const { payload } = action;
  const url = LicenseServiceUrls.authorizedDevicesUrl(config.baseApiUrl, undefined);
  const serverAction = CloudActions.authorizedDeviceDeactivate(payload.userId, payload.deviceId);

  try {
    const res = yield call(UserClientUtils.sendAction, config, serverAction, url);
    if (res.failed) {
      yield put(deactivateAuthorizedDeviceResult(false, res.error));
    } else if (res.succeeded) {
      yield put(deactivateAuthorizedDeviceResult(true, res.data));
    }
  } catch (error) {
    yield put(deactivateAuthorizedDeviceResult(false, error));
  }
}

export function* renameAuthorizedDeviceSaga(
  config: ClientConfig,
  action: RenameAuthorizedDeviceAction,
): Generator<unknown, void, SendActionResult> {
  const { payload } = action;
  const url = LicenseServiceUrls.authorizedDevicesUrl(config.baseApiUrl, undefined);
  const serverAction = CloudActions.authorizedDeviceRename(
    payload.userId,
    payload.deviceId,
    payload.newName,
  );

  try {
    const res = yield call(UserClientUtils.sendAction, config, serverAction, url);
    if (res.failed) {
      yield put(renameAuthorizedDeviceResult(false, res.error));
    } else if (res.succeeded) {
      yield put(renameAuthorizedDeviceResult(true, res.data));
    }
  } catch (error) {
    yield put(renameAuthorizedDeviceResult(false, error));
  }
}

function* createAuthorizedDeviceSaga(
  config: ClientConfig,
  action: CreateAuthorizedDeviceAction,
): Generator<unknown, void, SendActionResult> {
  const { payload } = action;
  const url = LicenseServiceUrls.authorizedDevicesUrl(config.baseApiUrl, 2);
  const serverAction = CloudActions.importAuthorizedDeviceFromFingerprint(
    payload.userId,
    payload.costCenterId,
    payload.productAccessId,
    payload.fingerprint,
    payload.deviceName,
  );

  try {
    const res = yield call(UserClientUtils.sendAction, config, serverAction, url);
    if (res.failed) {
      yield put(createAuthorizedDeviceResult(false, res.data));
    } else if (res.succeeded) {
      yield put(createAuthorizedDeviceResult(true, res.data));
    }
  } catch (error) {
    yield put(createAuthorizedDeviceResult(false, error));
  }
}

function* createExtendedOfflineTokenSaga(
  config: ClientConfig,
  {
    payload: { userId, authorizedDeviceId, startDate, endDate, productAccessId },
  }: CreateExtendedOfflineTokenAction,
): Generator<unknown, void, SendActionResult> {
  const url = LicenseServiceUrls.extendedOfflineTokensUrl(config.baseApiUrl, undefined);
  const serverAction = CloudActions.addExtendedOfflineToken(
    userId,
    authorizedDeviceId,
    startDate,
    endDate,
    productAccessId,
  );
  try {
    const res = yield call(UserClientUtils.sendAction, config, serverAction, url);

    if (res.failed) {
      yield put(createExtendedOfflineTokenResult(false, res.error));
    } else if (res.succeeded) {
      yield put(createExtendedOfflineTokenResult(true, res.data));
    }
  } catch (error) {
    yield put(createExtendedOfflineTokenResult(false, error));
  }
}

function* exportExtendedOfflineTokenSaga(
  config: ClientConfig,
  {
    payload: { primaryEmail, password, costCenterId, productAccessId, eotId },
  }: ExportExtendedOfflineTokenAction,
): Generator<unknown, void, SendActionResult> {
  const url = LicenseServiceUrls.extendedOfflineTokensUrl(config.baseApiUrl, 2);
  const serverAction = CloudActions.exportExtendedOfflineToken(
    primaryEmail,
    password,
    costCenterId,
    productAccessId,
    eotId,
  );
  try {
    const res = yield call(UserClientUtils.sendAction, config, serverAction, url);

    if (res.failed) {
      yield put(exportExtendedOfflineTokenResult(false, res.error));
    } else if (res.succeeded) {
      yield put(exportExtendedOfflineTokenResult(true, res.data));
    }
  } catch (error) {
    yield put(exportExtendedOfflineTokenResult(false, error));
  }
}

function* watchDeactivateAuthorizedDeviceSaga(
  config: ClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(DEACTIVATE_AUTHORIZED_DEVICE, deactivateAuthorizedDeviceSaga, config);
}

function* watchRenameAuthorizedDeviceSaga(config: ClientConfig): Generator<unknown, void, unknown> {
  yield takeLatest(RENAME_AUTHORIZED_DEVICE, renameAuthorizedDeviceSaga, config);
}

function* watchCreateAuthorizedDeviceSaga(config: ClientConfig): Generator<unknown, void, unknown> {
  yield takeLatest(CREATE_AUTHORIZED_DEVICE, createAuthorizedDeviceSaga, config);
}
function* watchCreateExtendedOfflineTokenSaga(
  config: ClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(CREATE_EXTENDED_OFFLINE_TOKEN, createExtendedOfflineTokenSaga, config);
}

function* watchExportExtendedOfflineTokenSaga(
  config: ClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(EXPORT_EXTENDED_OFFLINE_TOKEN_SAGA, exportExtendedOfflineTokenSaga, config);
}

// export wrapper function to capture client config for sagas to read from
export default (config: ClientConfig = {}) =>
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([
      watchDeactivateAuthorizedDeviceSaga(config),
      watchRenameAuthorizedDeviceSaga(config),
      watchCreateAuthorizedDeviceSaga(config),
      watchCreateExtendedOfflineTokenSaga(config),
      watchExportExtendedOfflineTokenSaga(config),
    ]);
  };
